<template>
	<div class="p4">
		<!-- Loop through config array and render components -->
		<div v-for="(item, i) in config" :key="i">
			<div v-if="item.componentName === 'j-base-select'">
				<core-form-input
					:componentName="item.componentName"
					:id="item.id"
					:size="size"
					:fsLabel="fsLabel"
					:name="item.id"
					:options="item.options"
					:description="item.description"
					:label="item.label"
					:subheadline="item.subheadline"
					v-validate="item.validate"
					v-model="form[item.id]"
					:validationErrors="errors"
					class="mb6"
				/>
			</div>
			<div v-else-if="item.showFieldWhen">
				<core-form-input
					:componentName="item.componentName"
					v-if="
						form[item.showFieldWhen.targetId] === item.showFieldWhen.targetValue
					"
					:id="item.id"
					:size="size"
					:fsLabel="fsLabel"
					:name="item.id"
					:options="item.options"
					:description="item.description"
					:label="item.label"
					:subheadline="item.subheadline"
					v-validate="item.validate"
					v-model="form[item.id]"
					:validationErrors="errors"
					class="mb6"
				/>
			</div>
			<div v-else>
				<core-form-input
					:componentName="item.componentName"
					:id="item.id"
					:size="size"
					:fsLabel="fsLabel"
					:name="item.id"
					:options="item.options"
					:description="item.description"
					:label="item.label"
					:subheadline="item.subheadline"
					v-validate="item.validate"
					v-model="form[item.id]"
					:validationErrors="errors"
					class="mb6"
				/>
			</div>
		</div>

		<j-button @click="handleUpdate">{{ $t("update") }}</j-button>
	</div>
</template>

<script>
import CoreFormInput from "@/modules/core/components/CoreFormInput.vue"
import JButton from "@/components/JButton/"
import api from "@/modules/core/api/"
import { transformDate } from "@/utils/"

export default {
	props: {
		/** id of entity */
		id: {
			type: String,
			required: true
		},

		/** Possible Values:
		 *
		 *  invidiual,
		 *  company,
		 *  objects,
		 *  contacts,
		 *  identities,
		 *  addresses,
		 *  facts,
		 *  relationships
		 *  */

		for: {
			type: String,
			required: true
		},

		/** used to identify contacts, identities, addresses, facts */
		updateId: String,

		/**
		 * Array of objects, each object represents input field
		 *
		 * Possible keys:
		 *
		 * componentName: - defines which component CoreFormInput will render
		 * id: - define id to use for this input
		 * label: - define label to display with input
		 * subheadline: - subheadline prop for CoreFormInput
		 * options: - option prop for CoreFormInput
		 * description - description prop for CoreFormInput
		 * validate - v-validate prop for CoreFormInput
		 * showFieldWhen : {targetValue: value , targetId: option } - renders as <div v-if="option === value"/>
		 * value: - defines value to prepopulate in input
		 *
		 */
		config: {
			type: Array,
			required: true
		},

		/*
		 * Size of input component
		 */
		size: { type: String, required: false, default: "medium" },

		/*
		 * Font size of labels
		 */
		fsLabel: { type: String, default: "fs4" }
	},

	components: {
		CoreFormInput,
		JButton
	},

	data() {
		return {
			form: {}
		}
	},

	methods: {
		prePopulateForm() {
			// Set form id from updateId prop
			this.$set(this.form, "id", this.updateId)

			// prepopulate fields based on passed config prop
			this.config.map(item => {
				this.$set(this.form, item.id, item.value)
			})
		},

		async handleUpdate() {
			// Validate form
			const isValid = await this.$validator.validate()
			if (!isValid) return

			// Validation for JInputObjectList
			if (
				this.config
					.map(item => item.componentName)
					.includes("j-input-object-list")
			) {
				const isObjectValid = await this.validateInputObjectList()
				if (!isObjectValid) return
			}

			let apiKeys = null
			let input = null

			// For Contacts
			if (this.for === "contacts") {
				apiKeys = "updateContact"

				let id = this.form.id
				let type =
					this.form.selectType === "Others"
						? this.form.otherType
						: this.form.selectType
				let value = this.form.value

				input = {
					entityId: this.id,
					updateContactInput: {
						id,
						type,
						value
					}
				}
			}

			// For Identities
			if (this.for === "identities") {
				apiKeys = "updateIdentity"

				let id = this.form.id
				let type =
					this.form.selectType === "Others"
						? this.form.otherType
						: this.form.selectType
				let value = this.form.value

				input = {
					entityId: this.id,
					updateIdentityInput: {
						id,
						type,
						value
					}
				}
			}

			// For Addresses
			if (this.for === "addresses") {
				let fields = this.form.fields

				apiKeys = "updateAddress"
				input = {
					entityId: this.id,
					updateAddressInput: {
						type: this.form.type,
						id: this.form.id,
						fields: fields
					}
				}
			}

			// For Facts
			if (this.for === "facts") {
				let value = null

				// compose updateFactInput.values
				if (this.form.factOptions === "dateValue")
					value = {
						dateValue: transformDate(this.form.dateValue)
					}
				else
					value = {
						[this.form.factOptions]: this.form[this.form.factOptions]
					}

				apiKeys = "updateFact"
				input = {
					entityId: this.id,
					updateFactInput: {
						id: this.form.id,
						type: this.form.type,
						value: value
					}
				}
			}

			// For Individual
			if (this.for === "individual") {
				const {
					name,
					gender,
					salutation,
					englishFirstName,
					englishLastName,
					chineseFirstName,
					chineseLastName,
					dateOfBirth,
					maritalStatus,
					occupation,
					countryOfResidency,
					acceptsMarketing,
					preferredCommunicationChannel,
					preferredLanguage
				} = this.form

				apiKeys = "updateIndividual"
				input = {
					id: this.id,
					updateIndividualInput: {
						nameFormat: name,
						gender,
						salutation,
						englishFirstName,
						englishLastName,
						chineseFirstName,
						chineseLastName,
						dateOfBirth: transformDate(dateOfBirth),
						maritalStatus,
						occupation,
						countryOfResidency,
						acceptsMarketing,
						preferredCommunicationChannel,
						preferredLanguage
					}
				}
			}

			// For Company
			if (this.for === "company") {
				apiKeys = "updateCompany"
				input = { id: this.id, updateCompanyInput: this.form }
			}

			// For Object
			if (this.for === "object") {
				apiKeys = "updateObject"
				input = { id: this.id, updateObjectInput: this.form }
			}

			// For Internal
			if (this.for === "internal") {
				const updateInternalInput = { ...this.form }

				const initialInternalCodeValue = this.config.find(
					item => item.id === "internalCode"
				).value

				// If internal code is the same, don't update it
				// otherwise API would return error that this code already exists
				if (
					initialInternalCodeValue === this.form.internalCode ||
					this.form.internalCode.trim() === ""
				)
					delete updateInternalInput.internalCode

				apiKeys = "updateInternal"
				input = {
					id: this.id,
					updateInternalInput: updateInternalInput
				}
			}

			if (this.for === "organization") {
				const updateOrganizationInput = {
					nameFormat: this.form.name,
					source: this.form.source,
					isActive: this.form.isActive
				}

				apiKeys = "updateOrganization"
				input = {
					id: this.id,
					updateOrganizationInput: updateOrganizationInput
				}
			}

			this.isLoading = true

			const res = await api[apiKeys](input)

			this.isLoading = false

			// Error
			if (res.errors) {
				this.$store.dispatch("showError", res.errors)
			}

			this.$emit("updated")
		},

		validateInputObjectList() {
			let hasMissingKeyOrValue =
				this.form.fields !== null
					? this.form.fields
							.map(item => {
								return !item.type || !item.value ? false : true
							})
							.includes(false)
					: true

			let array = this.form.fields.map(item => item.type)
			let hasDuplicateKey = array.some(item => {
				return array.indexOf(item) !== array.lastIndexOf(item)
			})

			return !hasMissingKeyOrValue && !hasDuplicateKey ? true : false
		}
	},
	created() {
		this.prePopulateForm()
	}
}
</script>
